/**
 * Dispatch bridge — single entry that routes the dialog's Generate / tool-control
 * action through the v0.7 execution-mode matrix.
 *
 * AI kind: branches by `capability` so we can use the legacy synchronous tool
 * endpoints (`/api/images/upscale`, `/api/images/remove-bg`) while the rest goes
 * through `/api/prompts/generate` + queue poll. This collapses the legacy
 * if-tree (pattern / upscale / remove_bg) into one capability-keyed branch.
 *
 * Client kind: looks up handler from `apps/creator-hub/src/client-handlers/registry.js`
 * (filled by H-031) and invokes it with `{ image, config, intent }`.
 *
 * Cutover note (per design.md §P2 cutover assertion): once worker
 * `/api/prompts/generate` absorbs `image_upscale` + `image_remove_bg`
 * capabilities, the AI-side branch can collapse further to a single POST.
 * Until then, the bridge keeps the synchronous tool calls but the dialog code
 * itself no longer special-cases execution_mode.
 */

import {
  getDispatchConfig,
  isAiMode,
  isClientMode,
  InvalidExecutionModeError,
} from '@mamamiya/platform/cards/execution-mode-matrix.js'
import { CONFIG } from '../../../core/config.js'
import { store } from '../../../core/store.js'
import { fetchWithTimeout, getSessionId, REQUEST_TIMEOUT_MS } from '../../../api/index.js'
import { upscaleImage, removeBackground } from '../../../api/ai-tools.js'
import { trackClientIntentUsed } from '../../../utils/tracking-funnel.js'
import { clientHandlers } from '../../../client-handlers/registry.js'

/**
 * @typedef {Object} JobInput
 * @property {string|null}  source_image_url
 * @property {{origin:'library'|'asset', id:string}|null} [source_ref]  typed source slot (ADR-005 #10)
 * @property {string|null}  reference_image_url
 * @property {string|null}  inspiration_image_url
 * @property {string|null}  text_input
 * @property {string|null}  custom_prompt
 * @property {string}       aspect_ratio
 * @property {Object|null}  vision_analysis
 * @property {string[]}     style_card_ids
 * @property {string|null}  negative_prompt
 */

/**
 * @typedef {Object} DispatchResult
 * @property {boolean}         success
 * @property {'ai'|'client'}   kind
 * @property {string}          [image_url]      AI kind: final image URL
 * @property {string}          [data_url]       Client kind: dataURL inline result
 * @property {string|number}   [generation_id]  AI kind: prompt_records.id
 * @property {Object}          [metadata]       Client kind: tool-specific metadata
 * @property {string}          [error]
 */

/**
 * Dispatch a Generate / tool-control event from the dialog.
 *
 * @param {Object} intent
 * @param {JobInput} jobInput
 * @param {Object} [hooks]
 * @param {(label: string) => void} [hooks.onProgress]   loading-text updater
 * @returns {Promise<DispatchResult>}
 */
export async function dispatchFromUi(intent, jobInput, hooks = {}) {
  const cfg = getDispatchConfig(intent.execution_mode)

  if (cfg.kind === 'client') {
    return runClientPath(intent, jobInput, cfg, hooks)
  }

  if (cfg.kind === 'ai') {
    return runAiPath(intent, jobInput, cfg, hooks)
  }

  throw new InvalidExecutionModeError(intent.execution_mode)
}

// ---- AI path ----

async function runAiPath(intent, jobInput, cfg, hooks) {
  const onProgress = hooks.onProgress || (() => {})

  switch (cfg.capability) {
    case 'image_upscale':
      return runUpscaleSync(jobInput.source_image_url, onProgress)
    case 'image_remove_bg':
      return runRemoveBgSync(jobInput.source_image_url, onProgress)
    default:
      return runSyncGenerate(intent, jobInput, onProgress)
  }
}

async function runUpscaleSync(sourceImageUrl, onProgress) {
  if (!sourceImageUrl) throw new Error('Upscale requires a source image')
  onProgress('Upscaling...')
  // 4x, matching the result-toolbar tool (image-tools.js) — nothing ever wrote
  // the old creation.upscaleScale store key, so the two paths silently diverged
  // (2 here vs 4 there). One product, one scale.
  const res = await upscaleImage(sourceImageUrl, 4)
  const url = res?.url || res?.preview_url
  if (!url) throw new Error('Upscale returned no image URL')
  return { success: true, kind: 'ai', image_url: url }
}

async function runRemoveBgSync(sourceImageUrl, onProgress) {
  if (!sourceImageUrl) throw new Error('Source image required for remove background')
  onProgress('Removing background...')
  const res = await removeBackground(sourceImageUrl, true)
  const url = res?.url || res?.preview_url
  if (!url) throw new Error('Remove BG returned no image URL')
  return { success: true, kind: 'ai', image_url: url }
}

async function runSyncGenerate(intent, jobInput, onProgress) {
  onProgress('Generating...')

  const headers = buildAuthHeaders()

  // create-runtime /api/customer/generate is SYNCHRONOUS (201 with the asset) —
  // no job queue / polling. The intent card drives the pipeline server-side: pass
  // its execution_mode as the required `intent` and include the card in card_ids.
  //
  // Image slots (ADR-005 #10): the SOURCE pixel base is a typed ref
  // `source: { origin, id }` resolved server-side per origin (library = system-
  // trusted images row; asset = owner-checked upload). This supersedes the
  // deprecated `reference_asset_id` — we send `source` when the slot was filled
  // and only fall back to `reference_asset_id` for an uploaded source that has
  // no typed ref. References are NOT a pixel slot — they were realized as cards
  // (in card_ids) via the vision path. why → ADR-005 #1/#5/#10.
  const card_ids = [intent.id, ...(jobInput.style_card_ids || [])].filter(Boolean)
  const sourceRef = jobInput.source_ref
  // ADR-005 2.4.1: reference IS a second pixel frame. Send `reference` when a
  // reference image was picked — the backend routes [source, reference] to the
  // two-image fal model (flux-pro/kontext/multi) for Style Transfer / Try-On /
  // Model Swap. Inspiration cards still compose into the prompt independently.
  const referenceRef = jobInput.reference_ref
  const res = await fetchWithTimeout(
    `${CONFIG.FRONTEND_API}/api/customer/generate`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        intent: intent.execution_mode || 'prompt',
        prompt: jobInput.text_input || jobInput.custom_prompt || '',
        card_ids,
        ...(sourceRef
          ? { source: sourceRef }
          : jobInput.reference_asset_id
            ? { reference_asset_id: jobInput.reference_asset_id }
            : {}),
        ...(referenceRef ? { reference: referenceRef } : {}),
        ...(jobInput.negative_prompt ? { negative_prompt: jobInput.negative_prompt } : {}),
        ...(jobInput.aspect_ratio ? { aspect_ratio: jobInput.aspect_ratio } : {}),
        // Per-card strength (ADR-005 2.5.0 — weighted cards). why → ADR-005 2.5.0.
        ...(jobInput.card_strengths && Object.keys(jobInput.card_strengths).length
          ? { card_strengths: jobInput.card_strengths }
          : {}),
      }),
    },
    REQUEST_TIMEOUT_MS.GENERATION
  )

  if (res.status === 429) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || err.msg || 'Too many requests, please wait a moment')
  }
  if (!res.ok) {
    const errText = await res.text()
    throw new Error(`Generation failed (${res.status}): ${errText.slice(0, 200)}`)
  }

  const body = await res.json()
  const asset = body.data?.asset || body.asset
  const image_url = asset?.public_url || asset?.url
  if (!image_url) throw new Error('Generation returned no image')
  // asset_id lets the "Refine this" loop send the result back as a TYPED source
  // ref ({origin:'asset', id}) — the backend rejects raw URLs (SSRF). why → ADR-005 #10.
  return {
    success: true,
    kind: 'ai',
    image_url,
    generation_id: body.data?.generation_id,
    asset_id: asset?.id,
  }
}

// ---- Client path ----

async function runClientPath(intent, jobInput, cfg, hooks) {
  const onProgress = hooks.onProgress || (() => {})
  const handler = clientHandlers[cfg.client_handler]
  if (!handler) {
    return {
      success: false,
      kind: 'client',
      error: `Tool not yet available — ${cfg.client_handler} is implemented in creator-client-tools-v07 (H-031).`,
    }
  }

  onProgress('Editing...')

  const config = (() => {
    if (!intent.client_tool_config) return {}
    try {
      return typeof intent.client_tool_config === 'string'
        ? JSON.parse(intent.client_tool_config)
        : intent.client_tool_config
    } catch {
      return {}
    }
  })()

  // Handlers register either as a bare async function or as an object with
  // a `run` method (registry.js contract) — all five F-class handlers use
  // the object shape, so calling `handler(...)` directly would throw.
  const input = {
    image: jobInput.source_image_url,
    config,
    intent,
  }
  const result = typeof handler === 'function' ? await handler(input) : await handler.run(input)

  trackClientIntentUsed(intent.id, intent.execution_mode)

  return {
    success: true,
    kind: 'client',
    image_url: result.result_image_url,
    data_url: result.result_data_url,
    metadata: result.metadata || {},
  }
}

// ---- Helpers ----

function buildAuthHeaders({ contentType = true } = {}) {
  const headers = {}
  if (contentType) headers['Content-Type'] = 'application/json'
  const token = store.getState('auth.token')
  if (token) {
    headers.Authorization = `Bearer ${token}`
  } else {
    headers['X-Session-ID'] = getSessionId()
  }
  return headers
}

export { isAiMode, isClientMode }
